import React, { useState, useRef } from "react";
import useSWR from "swr";
import api, { money, downloadFile } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Download, Shield, Upload, AlertTriangle, Package } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { EXP_CATEGORY } from "@/lib/labels";

const fetcher = (u) => api.get(u).then((r) => r.data);
const COLORS = ["#026FCB","#10B981","#F59E0B","#EF4444","#8B5CF6","#0EA5E9","#F97316","#84CC16","#EC4899","#6366F1"];

export default function Reports() {
  const { data: tours } = useSWR("/tours", fetcher);
  const { data: expenses } = useSWR("/expenses", fetcher);
  const { data: parts } = useSWR("/participants", fetcher);
  const [selTour, setSelTour] = useState("");
  const [backingUp, setBackingUp] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const runBackup = async () => {
    setBackingUp(true);
    try {
      const ts = new Date().toISOString().replace(/[-:T.]/g, "").slice(0, 14);
      await downloadFile("/admin/backup", `icemonkey_backup_${ts}.json`);
      toast.success("Copia de seguridad descargada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar la copia");
    } finally { setBackingUp(false); }
  };

  const [downloadingBundle, setDownloadingBundle] = useState(false);
  const runFullBundle = async () => {
    setDownloadingBundle(true);
    try {
      const ts = new Date().toISOString().replace(/[-:T.]/g, "").slice(0, 14);
      await downloadFile("/admin/download-full", `icemonkey_bundle_${ts}.zip`);
      toast.success("Bundle completo descargado (código + datos)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el bundle");
    } finally { setDownloadingBundle(false); }
  };

  const openRestore = () => {
    setRestoreFile(null);
    setRestoreResult(null);
    setConfirmText("");
    setRestoreOpen(true);
  };

  const runRestore = async () => {
    if (!restoreFile) return toast.error("Selecciona un archivo .json");
    if (confirmText.trim().toUpperCase() !== "RESTAURAR") return toast.error("Escribe RESTAURAR para confirmar");
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const { data } = await api.post("/admin/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRestoreResult(data);
      toast.success(`${data.total_upserted} registros restaurados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo restaurar");
    } finally { setRestoring(false); }
  };

  React.useEffect(() => { if (tours && !selTour && tours[0]) setSelTour(tours[0].id); }, [tours, selTour]);

  const { data: finance } = useSWR(selTour ? `/tours/${selTour}/finance` : null, fetcher);

  const byCat = {};
  (expenses || []).filter((e) => !selTour || e.tour_id === selTour).forEach((e) => {
    byCat[e.category] = (byCat[e.category] || 0) + Number(e.amount || 0) * Number(e.exchange_rate || 1);
  });
  const pie = Object.entries(byCat).map(([k, v]) => ({ name: EXP_CATEGORY[k] || k, value: Math.round(v) }));

  const byCountry = {};
  (parts || []).forEach((p) => { byCountry[p.country || "—"] = (byCountry[p.country || "—"] || 0) + 1; });
  const country = Object.entries(byCountry).map(([k, v]) => ({ country: k, participants: v }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Informes</h1><p className="text-slate-500 mt-1">Rentabilidad, gastos por categoría y participantes por país.</p></div>
        <div className="flex items-center gap-2">
          <Select value={selTour} onValueChange={setSelTour}>
            <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent>{(tours || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Restaurar datos desde JSON</DialogTitle>
          </DialogHeader>
          {!restoreResult ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 text-sm">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <div>
                  Restaurar sobrescribe los datos actuales (por id) y añade los nuevos. Los usuarios nunca se tocan.
                  Recomendado: haz una copia antes por si acaso.
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Archivo .json de backup</label>
                <input
                  ref={fileInputRef}
                  data-testid="restore-file-input"
                  type="file"
                  accept=".json,application/json"
                  onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                  className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                />
                {restoreFile && (
                  <div className="mt-2 text-xs text-slate-500 mono">
                    {restoreFile.name} · {(restoreFile.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Escribe <b>RESTAURAR</b> para confirmar</label>
                <input
                  data-testid="restore-confirm-input"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="RESTAURAR"
                  className="mt-1 block w-full rounded-md border border-slate-300 dark:border-[#25334D] bg-white dark:bg-[#0F1729] px-3 py-2 text-sm"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRestoreOpen(false)}>Cancelar</Button>
                <Button
                  data-testid="restore-run-btn"
                  onClick={runRestore}
                  disabled={restoring || !restoreFile || confirmText.trim().toUpperCase() !== "RESTAURAR"}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {restoring ? "Restaurando…" : "Restaurar ahora"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-center py-3">
                <div className="text-3xl font-bold text-emerald-600">{restoreResult.total_upserted}</div>
                <div className="text-sm text-slate-500">registros restaurados</div>
              </div>
              <div className="max-h-60 overflow-y-auto rounded-lg border border-slate-200 dark:border-[#25334D]">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-[#1A2336] text-xs uppercase text-slate-500">
                    <tr><th className="text-left px-3 py-2">Colección</th><th className="text-right px-3 py-2">Upserted</th><th className="text-right px-3 py-2">Errores</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(restoreResult.stats).map(([k, v]) => (
                      <tr key={k} className="border-t border-slate-100 dark:border-[#25334D]/50">
                        <td className="px-3 py-2">{k}</td>
                        <td className="px-3 py-2 text-right mono text-emerald-600">{v.upserted ?? "—"}</td>
                        <td className="px-3 py-2 text-right mono text-amber-600">{v.errors ?? (v.reason || "—")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DialogFooter>
                <Button onClick={() => { setRestoreOpen(false); window.location.reload(); }} className="bg-[#026FCB] hover:bg-[#0358a5]">
                  Cerrar y recargar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {finance && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4"><div className="text-xs mono uppercase text-slate-500">Coste / cliente</div><div className="text-xl font-bold mt-1">{money(finance.cost_per_client)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs mono uppercase text-slate-500">Beneficio / cliente</div><div className="text-xl font-bold mt-1 text-emerald-600">{money(finance.profit_per_client)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs mono uppercase text-slate-500">Break-even</div><div className="text-xl font-bold mt-1">{finance.break_even_pax} pax</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs mono uppercase text-slate-500">Margen previsto</div><div className={`text-xl font-bold mt-1 ${finance.gross_margin_expected >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(finance.gross_margin_expected)}</div></CardContent></Card>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card><CardHeader><CardTitle>Gastos por categoría</CardTitle></CardHeader><CardContent className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" outerRadius={110} label>
                {pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent></Card>
        <Card><CardHeader><CardTitle>Participantes por país</CardTitle></CardHeader><CardContent className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={country}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="country" tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="participants" fill="#026FCB" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent></Card>
      </div>

      {isAdmin && (
        <Card data-testid="backup-card" className="border-[#026FCB]/30 bg-gradient-to-r from-sky-50/60 to-white dark:from-sky-950/20 dark:to-transparent">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-[#026FCB]/10 text-[#026FCB] flex items-center justify-center shrink-0">
                  <Shield size={20} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold">Copia de seguridad</div>
                  <div className="text-sm text-slate-500 mt-0.5">
                    Descarga un archivo JSON con todos los datos (tours, clientes, pagos, hoteles, gastos…). Guárdalo en tu ordenador o en la nube. Recomendado: hazlo semanalmente.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  data-testid="backup-download-btn"
                  onClick={runBackup}
                  disabled={backingUp}
                  className="bg-[#026FCB] hover:bg-[#0358a5]"
                >
                  <Download size={16} className="mr-2" />
                  {backingUp ? "Generando…" : "Descargar copia"}
                </Button>
                <Button
                  data-testid="backup-restore-btn"
                  onClick={openRestore}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-950/40"
                >
                  <Upload size={16} className="mr-2" />
                  Restaurar datos
                </Button>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 dark:border-[#25334D] flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[#1A2336] text-slate-700 dark:text-slate-300 flex items-center justify-center shrink-0">
                  <Package size={20} />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold">Descarga completa (código + datos)</div>
                  <div className="text-sm text-slate-500 mt-0.5">
                    Un .zip con todo: código fuente del backend y frontend, scripts, configuración y el backup de datos actual. Útil para archivo o para levantar la app en otro sitio.
                  </div>
                </div>
              </div>
              <Button
                data-testid="bundle-download-btn"
                onClick={runFullBundle}
                disabled={downloadingBundle}
                variant="outline"
                className="border-slate-300 hover:bg-slate-50 dark:border-slate-700 shrink-0"
              >
                <Package size={16} className="mr-2" />
                {downloadingBundle ? "Empaquetando…" : "Descargar todo"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
